import React, { useEffect, useState } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/atoms'
import { ButtonType } from '@/components/atoms/button'
import { Popup } from '@/components/atoms/popup'
import { Checkbox } from '@/components/atoms/checkbox'
import { ReservationType } from '@/models/reservation'

type Props = {
  reservations: ReservationType[]
  onChange: (guestRoomAssignIds: string[] | undefined) => void
}

export const RoomFilterButton: React.FC<Props> = props => {
  const { t } = useTranslation()
  const [isShowFilterPopup, setIsShowFilterPopup] = useState<boolean>(false)
  const [selectedRooms, setSelectedRooms] = useState<string[]>([])

  const assignedRoomFilterOptions = () => {
    const options = props.reservations.flatMap(reserve =>
      reserve.assignedRoom.map(room => ({
        guestRoomAssignId: room.guestRoomAssignId as string,
        roomNumber: room.roomNumber as string,
        guestRoomAssignShortId: room.guestRoomAssignShortId as string,
      })),
    )
    return options
  }

  const isAllSelected = assignedRoomFilterOptions().length === selectedRooms.length

  useEffect(() => {
    const allGuestRoomAssignId = props.reservations.flatMap(reserve => reserve.assignedRoom.map(room => room.guestRoomAssignId as string))
    setSelectedRooms(allGuestRoomAssignId)
  }, [])

  useEffect(() => {
    props.onChange(isAllSelected ? undefined : selectedRooms)
  }, [selectedRooms])

  return (
    <div style={{ position: 'relative' }}>
      <Button
        icon={!isAllSelected ? require('@/static/images/icon_filter_active.svg') : require('@/static/images/icon_filter_disable.svg')}
        buttonType={!isAllSelected ? ButtonType.Simple : ButtonType.NonStyle}
        width={130}
        height={32}
        fontSize={12}
        onClick={() => setIsShowFilterPopup(!isShowFilterPopup)}
      >
        {t('Filter')}
      </Button>
      <Popup isOpen={isShowFilterPopup} onClose={() => setIsShowFilterPopup(false)} customStyle={css({ marginTop: 4, right: 0 })}>
        <div css={filterPopupStyle}>
          <div>
            <div className="title-block">
              <div>{t('Filter by rooms')}</div>
              {isAllSelected ? (
                <div className="sub-menu" onClick={() => setSelectedRooms([])}>
                  {t('Deselect All')}
                </div>
              ) : (
                <div className="sub-menu" onClick={() => setSelectedRooms(assignedRoomFilterOptions().map(room => room.guestRoomAssignId))}>
                  {t('Select All')}
                </div>
              )}
            </div>
            <div className="item-block">
              {assignedRoomFilterOptions().map(room => (
                <Checkbox
                  key={room.guestRoomAssignId}
                  value={selectedRooms.includes(room.guestRoomAssignId)}
                  label={`${room.roomNumber}(${room.guestRoomAssignShortId})`}
                  onChange={v => {
                    if (v) {
                      setSelectedRooms([...selectedRooms, room.guestRoomAssignId])
                    } else {
                      setSelectedRooms(selectedRooms.filter(id => id !== room.guestRoomAssignId))
                    }
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </Popup>
    </div>
  )
}

const filterPopupStyle = css({
  padding: 16,
  minWidth: 220,
  width: 'max-content',
  '.title-block': {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '14px !important',
    fontWeight: 'bold',
    '.sub-menu': {
      fontSize: '10px !important',
      cursor: 'pointer',
    },
  },
  '.item-block': {
    paddingTop: 8,
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'column',
    rowGap: 8,
  },
})
